import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
} from "@/components/ui/field";

const schema = z
  .object({
    password: z.string().min(8, "Mínimo 8 caracteres"),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: "Las contraseñas no coinciden",
    path: ["confirm"],
  });
type FormValues = z.infer<typeof schema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    if (!token) {
      toast.error("Enlace inválido o expirado");
      return;
    }
    const { error } = await authClient.resetPassword({
      newPassword: values.password,
      token,
    });
    if (error) {
      toast.error(error.message || "No pudimos restablecer tu contraseña");
      return;
    }
    toast.success("Contraseña actualizada");
    navigate("/auth/login");
  }

  return (
    <div>
      <h1 className="text-center text-3xl font-extrabold leading-tight tracking-tight">
        Nueva contraseña
      </h1>
      <p className="mt-0.5 text-center text-sm font-medium text-muted-foreground">
        Elige una contraseña segura para tu cuenta.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8">
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="password">
              Contraseña <span className="text-destructive">*</span>
            </FieldLabel>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className="pr-10"
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <FieldError errors={[errors.password]} />
          </Field>
          <Field>
            <FieldLabel htmlFor="confirm">
              Confirmar contraseña <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="confirm"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("confirm")}
            />
            <FieldError errors={[errors.confirm]} />
          </Field>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Guardando..." : "Restablecer contraseña"}
          </Button>
        </FieldGroup>
      </form>

      <p className="mt-8 text-center text-sm font-medium text-muted-foreground">
        Volver a{" "}
        <Link to="/auth/login" className="text-primary hover:underline">
          Iniciar Sesión
        </Link>
      </p>
    </div>
  );
}
